import React, { useEffect, useState } from 'react';
import { formatDate } from '../../utils/dateUtils';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button } from '@mui/material';
import { RequisitionService } from '../../services/requisition.service';
import { Requisition, REQUISITION_STATUS_LABELS } from '../../interfaces/requisition.interface';
import { useToast } from '../../context/ToastContext';
import SerialPostDialog from '../serial/SerialPostDialog'; // F6b
import { LineSerialsRequest } from '../../interfaces/inventory/serial.interface'; // F6b
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const PendingIssueRequisitionDetails: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    // B4-F + polish 11: "Raise PO" needs PURCHASE_ORDER_MANAGE plus the ability to see the requisition (the viewer's
    // own check: issuer / approver, REQUISITION_VIEW_ALL still accepted) -- mirrors RequisitionService.assertMaySeeRequisitionsForPo
    const canManagePo = usePermission(PERMISSIONS.PURCHASE_ORDER_MANAGE);
    const canViewAllReqs = usePermission(PERMISSIONS.REQUISITION_VIEW_ALL);
    const canIssue = usePermission(PERMISSIONS.REQUISITION_ISSUE);
    const canApproveReq = usePermission(PERMISSIONS.REQUISITION_APPROVE);
    const canRaisePo = canManagePo && (canViewAllReqs || canIssue || canApproveReq);
    const [requisition, setRequisition] = useState<Requisition | null>(null);
    const [loading, setLoading] = useState(true);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [issuing, setIssuing] = useState(false);

    useEffect(() => {
        const fetchRequisition = async () => {
            try {
                if (id) {
                    const response = await RequisitionService.getRequisitionById(id);
                    setRequisition(response);
                }
            } catch (err) {
                toast.showApiError(err, 'Failed to fetch requisition details');
            } finally {
                setLoading(false);
            }
        };

        fetchRequisition();
    }, [id]);

    const handleIssue = async (serials?: LineSerialsRequest) => {
        try {
            if (!requisition?.id) return;
            setIssuing(true);
            const issued = await RequisitionService.issueRequisition(requisition.id, serials);
            setConfirmOpen(false);   // F6b: only on success, so typed serials survive a refusal
            toast.showSuccess(`Requisition ${requisition.refNo} issued`);
            if (issued?.journalWarning) {
                toast.showError(issued.journalWarning);
            }
            navigate('/pending-issue-requisitions');
        } catch (err) {
            toast.showApiError(err, 'Failed to issue requisition');
        } finally {
            setIssuing(false);
        }
    };

    if (loading) return <Typography>Loading...</Typography>;
    if (!requisition) return <Typography>Requisition not found</Typography>;

    return (
        <Box sx={{ p: 3 }} data-help-page="store.issueReq.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Requisition Details</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {/* ---- B4-F ---- */}
                    {canRaisePo && requisition.id && (
                        <Button
                            data-help="store.issueReq.raisePo"
                            variant="outlined"
                            onClick={() => navigate(`/procurement/purchase-orders/new?requisitionId=${requisition.id}`)}
                            data-testid="raise-po-button"
                        >
                            Raise PO
                        </Button>
                    )}
                    {/* ---- end B4-F ---- */}
                    <Button
                        data-help="store.issueReq.issue"
                        variant="contained"
                        color="primary"
                        onClick={() => setConfirmOpen(true)}
                        data-testid="issue-button"
                    >
                        Issue Items
                    </Button>
                </Box>
            </Box>

            <Paper sx={{ mb: 3, p: 2 }} data-help-section="store.issueReq.header">
                <Typography variant="subtitle1">Reference No: {requisition.refNo}</Typography>
                <Typography variant="subtitle1">Status: {requisition.status ? REQUISITION_STATUS_LABELS[requisition.status] : '-'}</Typography>
                <Typography variant="subtitle1">Date: {requisition.date ? formatDate(requisition.date) : '-'}</Typography>
                <Typography variant="subtitle1">Requested By: {requisition.requisitionByName}</Typography>
                {requisition.remarks && (
                    <Typography variant="subtitle1" sx={{ whiteSpace: 'pre-wrap' }}>Purpose / remarks: {requisition.remarks}</Typography>
                )}
            </Paper>

            <TableContainer component={Paper} data-help-section="store.issueReq.lines">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="store.reqLine.col.item">Item Name</TableCell>
                            <TableCell data-help="store.reqLine.col.required" align="right">Required Quantity</TableCell>
                            <TableCell data-help="store.reqLine.col.approved" align="right">Approved Quantity</TableCell>
                            <TableCell data-help="store.reqLine.col.approvedDate">Approved Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {requisition.details?.map((detail) => (
                            <TableRow key={detail.id}>
                                <TableCell>{detail.itemName}</TableCell>
                                <TableCell align="right">{detail.requiredQty}</TableCell>
                                <TableCell align="right">{detail.approvedQty ?? '-'}</TableCell>
                                <TableCell>
                                    {detail.approvedDate
                                        ? formatDate(detail.approvedDate)
                                        : '-'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* F6b: serial-tracked lines pick one IN_STOCK serial per unit from the issuing store */}
            <SerialPostDialog
                open={confirmOpen}
                title="Issue these items?"
                message="The approved quantities will be taken out of stock and handed over to the requester. This cannot be undone."
                confirmLabel="Issue items"
                lines={(requisition.details ?? [])
                    .filter(d => d.id && (d.approvedQty ?? 0) > 0 && !d.issuedDate)
                    .map(d => ({ lineId: d.id!, itemId: d.itemId, label: d.itemName ?? 'Item', quantity: Number(d.approvedQty), mode: 'pick' as const, storeId: d.storeId }))}
                submitting={issuing}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={handleIssue}
            />
        </Box>
    );
};

export default PendingIssueRequisitionDetails;
